import { useState } from 'react';

import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import useCollections from '@/hooks/useCollections';
import { selectAccountSlug } from '@/redux/session/selectors';

import { MarkIncorrect } from './AmendRows';
import { NewTabStepLink, PlainRow } from './DetailsRow';
import { DocumentLine, useDocumentLink } from './DocumentLine';
import { buildKnowledgeSources, KnowledgeSource } from './knowledgeSources';
import { NAME_SLOT } from './StepRows';
import { ConversationTurn } from '../../../types/conversation';

type Props = { turn: ConversationTurn };

/** Rows kept visible while the list is collapsed. Every cited source is kept on top of these. */
const COLLAPSED_ROWS = 3;

export const useKnowledgeSources = (turn: ConversationTurn) => {
  const knowledgeBase = turn.metadata.knowledgeBase;
  const collectionId = knowledgeBase?.id ?? turn.metadata.collection?.collection_id;
  const contents = knowledgeBase?.contents ?? turn.metadata.collection?.fragments ?? [];

  const { collection, getStatus } = useCollections(collectionId);

  return {
    collectionId,
    collection,
    isLoadingCollection: getStatus === 'pending',
    ...buildKnowledgeSources(contents, turn.metadata.collection?.external_urls ?? []),
  };
};

/** `Knowledge base <name> triggered · N sources were retrieved` */
export const KnowledgeBaseHead = ({ turn }: Props) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const { collectionId, collection, isLoadingCollection, retrieved } = useKnowledgeSources(turn);

  if (!collectionId) {
    return null;
  }

  // Only call it deleted once the lookup has settled, or it flashes on every open.
  if (isLoadingCollection) {
    return <Skeleton className="h-5 w-64" />;
  }

  if (!collection) {
    return <PlainRow>{t('activityLogs.collection_deleted', { count: retrieved })}</PlainRow>;
  }

  return (
    <PlainRow>
      {t('collections.collection')}{' '}
      <NewTabStepLink
        to={slug ? `/${slug}/knowledge-bases/${collection.collection_id}` : null}
        tooltip={t('try_it.go_to_knowledge_base')}
      >
        {collection.name}
      </NewTabStepLink>{' '}
      {t('try_it.triggered')}
      {retrieved > 0 && ` · ${t('try_it.details.sources_retrieved', { count: retrieved })}`}
    </PlainRow>
  );
};

const ListToggle = ({ expanded, onToggle }: { expanded: boolean; onToggle: () => void }) => {
  const { t } = useTranslation();

  return (
    <Button
      variant="ghost-neutral"
      size="flush"
      aria-expanded={expanded}
      className="text-sm leading-5 font-normal text-foreground-muted hover:text-foreground"
      onClick={onToggle}
    >
      {expanded ? t('common.show_less') : t('common.show_more')}
      {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
    </Button>
  );
};

/** Every retrieved source, strongest first, collapsed to the first few. */
export const SourcesList = ({ turn }: Props) => {
  const [expanded, setExpanded] = useState(false);
  const { collectionId, collection, sources } = useKnowledgeSources(turn);

  if (!collectionId || !collection || sources.length === 0) {
    return null;
  }

  // A cited source stays visible while collapsed even when it scores low — it is
  // the one the answer actually used.
  const collapsed = sources.filter((source, index) => index < COLLAPSED_ROWS || source.isSelected);
  const visible = expanded ? sources : collapsed;

  return (
    <div className="flex flex-col items-start gap-4 self-stretch pl-1">
      <ol className="flex flex-col items-start gap-3 self-stretch">
        {visible.map((source) => (
          <DocumentLine
            key={source.key}
            collectionId={collectionId}
            datasourceId={source.datasourceId}
            documentId={source.documentId}
            name={source.name}
            score={source.score}
            isSelected={source.isSelected}
          />
        ))}
      </ol>
      {sources.length > collapsed.length && (
        <ListToggle expanded={expanded} onToggle={() => setExpanded((value) => !value)} />
      )}
    </div>
  );
};

const TopMatchLink = ({
  collectionId,
  source,
}: {
  collectionId: string;
  source: KnowledgeSource;
}) => {
  const { t } = useTranslation();
  const to = useDocumentLink(collectionId, source.datasourceId, source.documentId);

  return (
    <NewTabStepLink to={to} tooltip={t('try_it.details.go_to_document')} className="inline">
      {source.name}
    </NewTabStepLink>
  );
};

/** `N sources were selected · top match <name>`, falling back to what did not happen. */
export const SelectedSourcesHead = ({ turn }: Props) => {
  const { t } = useTranslation();
  const { collectionId, retrieved, selected, topMatch } = useKnowledgeSources(turn);
  const [before, after = ''] = t('try_it.details.top_match', { 0: NAME_SLOT }).split(NAME_SLOT);

  if (selected === 0) {
    return (
      <div className="flex flex-col items-start gap-4 pb-2">
        <PlainRow>
          {retrieved === 0
            ? t('try_it.details.no_sources_retrieved')
            : t('try_it.details.no_sources_selected')}
        </PlainRow>
        <MarkIncorrect turn={turn} />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-start gap-4 pb-2">
      <PlainRow>
        {t('try_it.details.sources_selected', { count: selected })}
        {topMatch?.name && collectionId && (
          <>
            {' · '}
            {before}
            <TopMatchLink collectionId={collectionId} source={topMatch} />
            {after}
          </>
        )}
      </PlainRow>
      <MarkIncorrect turn={turn} />
    </div>
  );
};
